import apiClient from './apiClient';
import { DocumentItem, DocDiff } from '../types/storage';

export interface EventPreviewChunks {
  identificazione?: string;
  temporale?: string;
  luogo?: string;
  target_accesso?: string;
  contenuto?: string;
  persone_org?: string;
  contatti_media?: string;
  metadati?: string;
}

export interface EventPreview {
  doc_id: string;
  titolo: string;
  relevance_score?: number;
  chunks: EventPreviewChunks;
  date_slots?: { date: string; time: string; label?: string; detail?: string }[];
}

export type EventSuppressedItem = DocumentItem & {
  is_suppressed?: boolean;
  suppressed_reason?: string | null;
  suppressed_at?: string | null;
};

class EventiService {
  listDocuments = async (): Promise<DocumentItem[]> => {
    const res = await apiClient.get('/api/eventi/documents');
    return res.data?.data ?? [];
  };

  listModifiedDocuments = async (): Promise<DocumentItem[]> => {
    const res = await apiClient.get('/api/eventi/documents/modified');
    return res.data?.items ?? res.data?.data ?? [];
  };

  listRemovedDocuments = async (): Promise<EventSuppressedItem[]> => {
    const res = await apiClient.get('/api/eventi/documents/suppressed');
    return res.data?.items ?? [];
  };

  getDocumentText = async (docId: string): Promise<string> => {
    const res = await apiClient.get(`/api/eventi/documents/${docId}/text`);
    return res.data?.content ?? res.data?.text ?? '';
  };

  getDocumentDiff = async (docId: string): Promise<DocDiff> => {
    const res = await apiClient.get(`/api/eventi/documents/${docId}/diff`);
    return res.data?.diff ?? {};
  };

  approveDocument = (docId: string) =>
    apiClient.post(`/api/eventi/documents/${docId}/approve`);

  rejectDocument = (docId: string) =>
    apiClient.post(`/api/eventi/documents/${docId}/reject`);

  suppressDocument = (docId: string, reason: string) =>
    apiClient.post(`/api/eventi/documents/${docId}/suppress`, { reason });

  unsuppressDocument = (docId: string) =>
    apiClient.delete(`/api/eventi/documents/${docId}/suppress`);

  deleteDocument = (docId: string) =>
    apiClient.delete(`/api/eventi/documents/${docId}`);

  triggerScraping = () =>
    apiClient.post('/api/eventi/trigger-scraping');

  smartSync = () =>
    apiClient.post('/api/eventi/smart-sync');

  rescrapeEvent = (docId: string, filename: string) =>
    apiClient.post('/api/eventi/rescrape', { doc_id: docId, filename });

  rescrapeAll = () =>
    apiClient.post('/api/eventi/rescrape-all');

  manualSave = (docId: string, text: string, editedBy?: string) =>
    apiClient.post(`/api/eventi/documents/${docId}/manual-edit`, {
      text,
      updated_by: editedBy,
    });

  getEventPreview = async (docId: string): Promise<EventPreview | null> => {
    try {
      const res = await apiClient.get(`/api/eventi/preview/${docId}`);
      const events = res.data?.events;
      return Array.isArray(events) && events.length > 0 ? events[0] : null;
    } catch {
      return null;
    }
  };
}

export const eventiService = new EventiService();
